import { useMediaQuery, useTheme } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ConsentField from '../../components/ConsentField/ConsentField';
import Description from '../../components/Description/Description';
import { ErrorMessage } from '../../components/ErrorMessage';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { ConsumerLoanTargetPath } from '../../domain/types';
import { EventApplicationSubmitted } from '../../helpers/gtm';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { getPath } from '../../helpers/url';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useGetConsentService from '../../hooks/useGetConsentService';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useSetRightPanelContent from '../../hooks/useSetRightPanelContent';
import useSettings from '../../hooks/useSettings';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { submitApplication } from '../../store/application/application';
import { addOrUpdateFormField, invalidateForm } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { selectGovernmentIdPageIndex, setCurrentPage, WizardPageAction } from '../../store/wizard/wizard';
import { AttributeNames } from '../../types/AttributeNames';
import SubmitPageSidePanel from './SubmitPageSidePanel';

// Fallback wizard index of VerifyIncomePageNO (the government-id page) in the NO/SE submit wizards. Only
// used if that page never recorded its actual position (it always does before Submit is reachable). See
// selectGovernmentIdPageIndex / setGovernmentIdPageIndex (LP-2699). Exported so tests assert against the
// same value rather than duplicating the literal.
export const VERIFY_INCOME_PAGE_FALLBACK_INDEX = 5;

const SubmitPage = () => {
    useComponentTracker('SubmitPage');
    const translate = useTranslation();
    const theme = useTheme();
    const greaterThanLg = useMediaQuery(theme.breakpoints.up('lg'));
    const { totalSections, totalAboutTheLoanPages } = usePagesConfig();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { currentPage } = usePagesContext();
    const { languageCode, countryCode } = useSettings();
    const applicationId = useAppSelector((state) => state.application.applicationId);
    const [errorOccurred, setErrorOccurred] = useState<boolean>(false);
    const { consentDetails, isError } = useGetConsentService('TERMS_LOANS');
    const defaultPagesAction = useDefaultPagesAction();
    const coApplicantGovernmentId = useAppSelector(selectFieldValue)(AttributeNames.coapplicantGovernmentId);
    const governmentIdPageIndex = useAppSelector(selectGovernmentIdPageIndex);
    const pagesActions: WizardPageAction[] = coApplicantGovernmentId ? ['continue-with-coapplicant'] : ['submit'];

    useSetBannerType('none');

    const sidePanelContent = useMemo(() => <SubmitPageSidePanel />, []);
    useSetRightPanelContent(greaterThanLg ? sidePanelContent : null);

    const requiredFieldConsents =
        consentDetails
            ?.filter((consentDetails) => consentDetails.required)
            .map((consentDetails) => consentDetails.name) ?? [];

    // Validate the required consents plus applicantGovernmentId (collected earlier, on VerifyIncomePageNO).
    const fieldsToValidate = [...requiredFieldConsents, AttributeNames.applicantGovernmentId];
    const validationErrors = useValidation(fieldsToValidate);

    useEffect(() => {
        setErrorOccurred(false);
    }, []);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'submit' || action === 'continue-with-coapplicant') {
            if (validationErrors.length) {
                // Persist the errors so the affected fields render their error state (including the
                // government-id field once we route back to VerifyIncomePageNO below).
                validationErrors.forEach((validationError) => {
                    dispatch(addOrUpdateFormField(validationError));
                });

                const governmentIdMissing = validationErrors.some(
                    (validationError) => validationError.fieldName === AttributeNames.applicantGovernmentId,
                );
                if (governmentIdMissing) {
                    // The government-id field is collected on VerifyIncomePageNO, not on this page. Rather
                    // than silently submitting an empty value and NPE-ing the backend (LP-2699), route the
                    // user back to re-collect/re-verify it, using the index that page recorded for the
                    // active wizard.
                    dispatch(setCurrentPage(governmentIdPageIndex ?? VERIFY_INCOME_PAGE_FALLBACK_INDEX));
                } else {
                    scrollToFieldWithError(validationErrors);
                }
                return false;
            }
            try {
                const response = await dispatch(submitApplication()).unwrap();
                dispatch(invalidateForm());
                navigate(
                    getPath(
                        ConsumerLoanTargetPath.ongoing,
                        languageCode,
                        countryCode,
                        response.applicationReferenceNumber,
                    ),
                );
            } catch (error) {
                setErrorOccurred(true);
                throw error;
            } finally {
                EventApplicationSubmitted(applicationId, countryCode, 'main');
            }
            return false;
        } else {
            return defaultPagesAction(action);
        }
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
            <Title text={translate('page.submit.title')} />
            <Description text={translate('page.submit.description')} />

            {!greaterThanLg && <SubmitPageSidePanel />}

            <Subtitle text={translate('page.submit.subtitle.consent')} />
            {consentDetails?.map((consentDetail) => (
                <ConsentField key={consentDetail.name} consentDetail={consentDetail} />
            ))}
            {errorOccurred && (
                <ErrorMessage
                    title={translate(`page.submit.error.title`)}
                    description={translate(`page.submit.error.description`)}
                />
            )}
            {isError && (
                <ErrorMessage title={translate('page.error.title')} description={translate('page.error.description')} />
            )}
            <PagesFooter
                actions={pagesActions}
                onAction={onAction}
                currentSection={1}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutTheLoanPages}
                currentPageInSection={1}
                sectionTitle={translate('field.section.about-the-loan')}
            />
        </>
    );
};

export default SubmitPage;
